import { useMemo } from 'react';

import { OnboardingActions } from '../../onboarding/onboarding-actions';
import { OnboardingProgress, ProgressStage } from '../../onboarding/onboarding-progress';

import type { OnboardingBuilding, ImportCompanyCandidate } from '../../onboarding/onboarding.types';
import type { WizardBuildingPackage } from './building-wizard.types';

const PACKAGE_LABELS: Record<string, string> = {
  Portfolio: 'Portefølje',
  Management: 'Forvaltning',
  Eos: 'Energi',
};

interface StepGjennomgangProps {
  buildings: OnboardingBuilding[];
  importCompanies: ImportCompanyCandidate[];
  packagesByBuildingId: Record<string, WizardBuildingPackage[]>;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  submitError: string | null;
}

/**
 * Final review screen for the in-tenant building wizard. Mirrors the portfolio
 * onboarding "Gjennomgang" step in shape — same stage list visualisation during
 * submit, same retry-on-error path — but lists buildings + their package
 * selections instead of company / users (which already exist on the tenant).
 */
export function StepGjennomgang({
  buildings,
  importCompanies,
  packagesByBuildingId,
  onBack,
  onSubmit,
  isSubmitting,
  submitError,
}: StepGjennomgangProps) {
  const selectedBuildings = useMemo(() => buildings.filter((b) => b.selected), [buildings]);

  const totalBra = useMemo(
    () => selectedBuildings.reduce((sum, b) => sum + b.bra, 0),
    [selectedBuildings],
  );

  const selectedImports = useMemo(
    () => importCompanies.filter((c) => c.selected),
    [importCompanies],
  );

  const progressStages: ProgressStage[] = useMemo(
    () => [
      { key: 'buildings', label: `Oppretter bygg (${selectedBuildings.length})` },
      { key: 'packages', label: 'Tildeler pakker' },
      {
        key: 'imports',
        label: `Henter og lagrer leietakere (${selectedImports.length})`,
        skip: selectedImports.length === 0,
      },
      { key: 'finalize', label: 'Fullfører' },
    ],
    [selectedBuildings.length, selectedImports.length],
  );

  const showProgress = isSubmitting || !!submitError;

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Gjennomgang</h2>
      <p className="text-white/60 mb-8">
        {showProgress
          ? 'Vi oppretter byggene nå…'
          : 'Kontroller at alt ser riktig ut før du fullfører.'}
      </p>

      {showProgress ? (
        <OnboardingProgress
          stages={progressStages}
          isSubmitting={isSubmitting}
          isComplete={false}
          error={submitError}
        />
      ) : (
        <>
          <section className="mb-6">
            <h3 className="text-xs font-semibold tracking-wider text-white/40 uppercase mb-3">
              Bygg ({selectedBuildings.length}) · {totalBra.toLocaleString('nb-NO')} m²
            </h3>
            {selectedBuildings.length > 0 ? (
              <div className="space-y-2">
                {selectedBuildings.map((building) => {
                  const pkgs = packagesByBuildingId[building.id] ?? [];
                  return (
                    <div
                      key={building.id}
                      className="p-3 rounded-lg border border-border bg-surface-base"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-white text-sm font-medium">
                            {building.name ?? building.address}
                          </span>
                          <span className="text-white/40 text-sm ml-2">
                            {building.postalCode} {building.city}
                            {building.countryThreeLetterCode && building.countryThreeLetterCode !== 'NOR' && (
                              <> ({building.countryThreeLetterCode})</>
                            )}
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-white/50 text-sm">
                            {building.bra.toLocaleString('nb-NO')} m²
                          </span>
                          <span className="text-sm font-medium text-brand-primary">
                            {building.role || '—'}
                          </span>
                        </div>
                      </div>
                      {pkgs.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {pkgs.map((p) => (
                            <span
                              key={p.packageType}
                              className="px-2 py-0.5 rounded-full text-xs bg-brand-primary/10 text-brand-primary border border-brand-primary/30"
                            >
                              {PACKAGE_LABELS[p.packageType] ?? p.packageType} · {p.tier}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 rounded-lg border border-border bg-surface-base text-white/50 text-sm">
                Ingen bygg valgt.
              </div>
            )}
          </section>

          <section className="mb-6">
            <h3 className="text-xs font-semibold tracking-wider text-white/40 uppercase mb-3">
              Leietakere ({selectedImports.length})
            </h3>
            {selectedImports.length > 0 ? (
              <div className="p-4 rounded-lg border border-border bg-surface-base">
                <div className="text-white text-sm">
                  {selectedImports.length} {selectedImports.length === 1 ? 'selskap' : 'selskaper'} vil
                  bli opprettet som mulige leietakere.
                </div>
                <div className="text-white/40 text-xs mt-1">
                  Selskaper som allerede finnes knyttes til kontoen uten duplisering.
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-lg border border-border bg-surface-base text-white/50 text-sm">
                Ingen leietakere valgt.
              </div>
            )}
          </section>
        </>
      )}

      {!showProgress && (
        <OnboardingActions
          onBack={onBack}
          onNext={onSubmit}
          nextLabel="Fullfør →"
          nextDisabled={isSubmitting || selectedBuildings.length === 0}
        />
      )}

      {showProgress && submitError && (
        <OnboardingActions onBack={onBack} onNext={onSubmit} nextLabel="Prøv igjen" />
      )}
    </div>
  );
}
